import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2, RefreshCw, TrendingUp, AlertTriangle, HelpCircle, Settings2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../EmptyState';
import PipelineSettingsCard from './PipelineSettingsCard';
import StartEngagementModal from './StartEngagementModal';

/**
 * BD-13 — tab 5, Pipeline: the pipeline review.
 *
 * An administrator prepares a review (reads Zoho, proposes changes in Approvals,
 * never writes), sees every open deal ranked by the priority field, and
 * downloads the firm pack. Anyone else sees their own deals in the review.
 */

interface ReviewDeal {
  id: number;
  zoho_deal_id: string;
  deal_name: string | null;
  owner_name: string | null;
  rank: string | null;
  rank_sort: number;
  snapshot: { stage?: string; close_date?: string; next_step?: string; amount?: number; account?: string };
  changes: { field: string; text: string }[];
  activity: {
    last_activity_at?: string | null; meetings?: { id: number; title: string; at: string }[]; debrief_summary?: string[]; threads?: { subject: string; state: string }[];
    // BD-11 / BD-16: the next booked meeting, open commitments, open follow-ups
    next_meeting?: { id: number; title: string; at: string } | null;
    commitments?: { id: number; description: string; due_date: string | null; overdue: boolean }[];
    follow_ups?: { id: number; subject: string | null; due_at: string | null; since: string | null }[];
  };
  issues: { code: string; text: string }[];
  questions: { code: string; text: string; audience: string }[];
  proposal_ids: number[];
}
interface Review { id: number; review_date: string; meeting_name: string; prepared_at: string | null; summary: Record<string, any> }

const SKIP_LABELS: Record<string, string> = {
  step_off: 'Pipeline review step is off',
  no_mapping: 'Slack not linked',
  not_permitted: 'Slack notifications off',
  duplicate: 'already sent',
  quiet_hours: 'quiet hours (sent when they end)',
  not_installed: 'Slack app not installed',
  token_unavailable: 'Slack is installed but its token cannot be read on this server',
  recent_failure: 'Slack refused it recently',
};

const days = (iso?: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const dayOf = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const PipelineTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [reviewList, setReviewList] = useState<Review[]>([]);
  const [review, setReview] = useState<Review | null>(null);
  const [deals, setDeals] = useState<ReviewDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [startFromDeal, setStartFromDeal] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const openReview = useCallback(async (id: number) => {
    const { data } = await api.get(`/api/bd/pipeline/reviews/${id}`);
    setReview(data.review);
    setDeals(data.deals || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/bd/pipeline/reviews');
      setReviewList(data.reviews || []);
      if (data.reviews && data.reviews.length) await openReview(data.reviews[0].id);
    } catch {
      toast.error('Could not load the pipeline reviews');
    } finally {
      setLoading(false);
    }
  }, [openReview]);

  useEffect(() => { load(); }, [load]);

  const prepare = async () => {
    setPreparing(true);
    try {
      const { data } = await api.post('/api/bd/pipeline/reviews', {});
      toast.success(`Prepared: ${data.summary.deals} deals, ${data.summary.proposals_made} new proposals, ${data.summary.questions} questions`);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not prepare the review');
    } finally {
      setPreparing(false);
    }
  };

  const downloadPack = async () => {
    if (!review) return;
    setDownloading(true);
    try {
      const res = await api.get(`/api/bd/pipeline/reviews/${review.id}/pack`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${review.meeting_name} ${review.review_date}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not build the pack');
    } finally {
      setDownloading(false);
    }
  };

  // Each person's bundle now, instead of at their scheduled time (same gates and once-only rule).
  const sendToSlack = async () => {
    if (!review) return;
    setSending(true);
    try {
      const { data } = await api.post(`/api/bd/pipeline/reviews/${review.id}/slack`, {});
      const results: { name: string | null; status: string; reason?: string }[] = data.results || [];
      const sent = results.filter((r) => r.status === 'sent').map((r) => r.name || 'someone');
      const notSent = results.filter((r) => r.status !== 'sent').map((r) => `${r.name || 'someone'}: ${SKIP_LABELS[r.reason || ''] || r.reason || r.status}`);
      const lines = [sent.length ? `Sent to ${sent.join(', ')}` : 'Nothing sent', ...notSent].join('\n');
      if (!results.length) toast('Nobody has deals or questions in this review.');
      else if (sent.length) toast.success(lines, { duration: 8000 });
      else toast.error(lines, { duration: 8000 });
      await openReview(review.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not send the review to Slack');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading the pipeline…</div>;

  return (
    <div className="space-y-4" data-testid="bd-pipeline-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{review ? `${review.meeting_name} · ${review.review_date}` : 'Pipeline review'}</h2>
          <p className="text-xs text-gray-500">
            Open deals ranked by priority, with the changes waiting for approval and the questions for each owner. Nothing is written to Zoho without an approval.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reviewList.length > 1 && (
            <select className="text-sm rounded border border-gray-300 px-2 py-1" value={review?.id || ''} onChange={(e) => openReview(Number(e.target.value))} data-testid="bd-pipeline-review-select">
              {reviewList.map((r) => <option key={r.id} value={r.id}>{r.review_date}</option>)}
            </select>
          )}
          {isAdmin && (
            <>
              <button type="button" onClick={() => setShowSettings((v) => !v)} className="text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5">
                <Settings2 className="w-4 h-4" /> Settings
              </button>
              <button type="button" onClick={prepare} disabled={preparing} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="bd-pipeline-prepare">
                {preparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Prepare review
              </button>
              {review && deals.length > 0 && (
                <button type="button" onClick={downloadPack} disabled={downloading} className="text-sm px-3 py-1.5 rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="bd-pipeline-pack">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Firm pack
                </button>
              )}
              {review && review.prepared_at && deals.length > 0 && (
                <button type="button" onClick={sendToSlack} disabled={sending} className="text-sm px-3 py-1.5 rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="bd-pipeline-slack" title="Sent automatically the evening before (or the morning of). This sends it now.">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to Slack
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isAdmin && showSettings && <PipelineSettingsCard />}

      {review && review.summary && review.summary.deals !== undefined && (
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <span>{review.summary.deals} deals</span>
          <span>{review.summary.proposals_made} new proposals</span>
          <span>{review.summary.questions} questions</span>
          {review.summary.duplicates && (review.summary.duplicates.contacts > 0 || review.summary.duplicates.accounts > 0) && (
            <span>{review.summary.duplicates.contacts} duplicate contact group(s), {review.summary.duplicates.accounts} duplicate account group(s) proposed for merging</span>
          )}
          {review.prepared_at && <span className="text-gray-400">prepared {new Date(review.prepared_at).toLocaleString()}</span>}
          {review.summary.slack?.sent_to?.length > 0 && (
            <span className="text-gray-400">sent in Slack to {review.summary.slack.sent_to.length} {review.summary.slack.sent_to.length === 1 ? 'person' : 'people'}</span>
          )}
          {review.summary.complete === false && <span className="text-amber-700">Only the first 2,000 deals were read.</span>}
        </div>
      )}

      {!review && (
        <EmptyState
          icon={TrendingUp}
          title="No pipeline review yet"
          description={isAdmin ? 'Prepare a review to read the open deals from Zoho, check their hygiene and list the questions for each owner.' : 'An administrator prepares the review before the pipeline meeting.'}
        />
      )}

      {review && deals.length === 0 && (
        <div className="text-center py-10 text-gray-400 border border-dashed rounded-lg">{isAdmin ? 'No open deals in this review.' : 'None of your deals are in this review.'}</div>
      )}

      {deals.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm" data-testid="bd-pipeline-deals">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Rank</th>
                <th className="px-3 py-2 text-left">Deal</th>
                <th className="px-3 py-2 text-left">Stage · Close</th>
                <th className="px-3 py-2 text-left">Next step</th>
                <th className="px-3 py-2 text-left">Since last review</th>
                <th className="px-3 py-2 text-left">Asks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deals.map((d) => {
                const quiet = days(d.activity?.last_activity_at);
                return (
                  <tr key={d.id} className="align-top" data-testid={`bd-pipeline-deal-${d.zoho_deal_id}`}>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{d.rank}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{d.deal_name || `Deal ${d.zoho_deal_id}`}</div>
                      <div className="text-xs text-gray-500">{[d.snapshot?.account, d.owner_name || 'No Jarvis owner'].filter(Boolean).join(' · ')}</div>
                      <div className="text-xs text-gray-400">{quiet === null ? 'No activity on record' : `Last activity ${quiet} day${quiet === 1 ? '' : 's'} ago`}</div>
                      <button type="button" onClick={() => setStartFromDeal(d.zoho_deal_id)} className="mt-1 text-xs text-blue-600 hover:underline" data-testid={`bd-start-engagement-${d.zoho_deal_id}`}>Start engagement</button>
                      {(d.activity?.debrief_summary || []).map((line, i) => <div key={i} className="text-xs text-gray-600 mt-0.5">“{line}”</div>)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{d.snapshot?.stage || '—'}<div className="text-xs text-gray-500">{d.snapshot?.close_date || 'no close date'}</div></td>
                    <td className="px-3 py-2 max-w-xs">
                      {d.snapshot?.next_step || <span className="text-gray-400">No next step</span>}
                      {d.activity?.next_meeting && (
                        <div className="text-xs text-gray-600 mt-1" data-testid={`bd-pipeline-next-meeting-${d.zoho_deal_id}`}>
                          Next meeting: {d.activity.next_meeting.title} · {dayOf(d.activity.next_meeting.at)}
                        </div>
                      )}
                      {(d.activity?.commitments || []).map((c) => (
                        <div key={c.id} className={`text-xs mt-0.5 ${c.overdue ? 'text-red-700' : 'text-gray-600'}`}>
                          Committed: {c.description}{c.due_date ? ` · ${c.overdue ? 'was due' : 'due'} ${c.due_date}` : ''}
                        </div>
                      ))}
                      {(d.activity?.follow_ups || []).map((f) => (
                        <div key={f.id} className="text-xs text-gray-600 mt-0.5">
                          Waiting on them{f.subject ? `: ${f.subject}` : ''}{f.due_at ? ` · follow up ${dayOf(f.due_at)}` : ''}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-xs">
                      {d.changes.map((c, i) => <div key={i}>{c.text}</div>)}
                      {!d.changes.length && <span className="text-gray-400">No change</span>}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-sm">
                      {d.questions.map((q, i) => (
                        <div key={i} className="flex gap-1 text-gray-700"><HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />{q.text}{q.audience === 'partner' ? ' (partner)' : ''}</div>
                      ))}
                      {d.proposal_ids.length > 0 && (
                        <button type="button" onClick={() => navigate('/app/business-development?tab=approvals')} className="mt-1 inline-flex items-center gap-1 text-amber-700 hover:underline">
                          <AlertTriangle className="w-3.5 h-3.5" /> {d.proposal_ids.length} change{d.proposal_ids.length === 1 ? '' : 's'} to approve
                        </button>
                      )}
                      {!d.questions.length && !d.proposal_ids.length && <span className="text-gray-400">Nothing to ask</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {startFromDeal && <StartEngagementModal dealId={startFromDeal} onClose={() => setStartFromDeal(null)} />}
    </div>
  );
};

export default PipelineTab;
